var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { memo, useCallback } from 'react';
import { OpenCampaignCardOverlayActive, OpenCampaignCardVariant, OpenCampaignCard as LUXOpenCampaignCard, } from '@lux/campaign-card';
import { screenSizes } from '@lux/core';
import { useMatchMedia } from '@lux/hooks';
import { PlusBenefitState, PlusBenefitType } from '@pegasus/plus';
import { Translate, useLocale } from '@pegasus/translation';
import { trackSelectPromotion } from '@browsing/tracking';
import { ErrorBoundary } from '@browsing/components';
import { getCampaignUrl } from '../utils';
import { CampaignCardContent } from './campaign-card-content';
import { CampaignCardMedia } from './campaign-card-media';
import { CampaignCardOverlay } from './campaign-card-overlay';
var isEarlyAccess = function (campaign) {
    if (!(campaign === null || campaign === void 0 ? void 0 : campaign.plusBenefit)) {
        return false;
    }
    var _a = campaign.plusBenefit, type = _a.type, state = _a.state;
    return type === PlusBenefitType.EarlyAccess && state === PlusBenefitState.Active;
};
var isExclusive = function (campaign) {
    if (!(campaign === null || campaign === void 0 ? void 0 : campaign.plusBenefit)) {
        return false;
    }
    var type = campaign.plusBenefit.type;
    return type === PlusBenefitType.Exclusive;
};
var OpenCampaignCardInternal = memo(function (_a) {
    var aspectRatio = _a.aspectRatio, aspectRatioXs = _a.aspectRatioXs, aspectRatioSm = _a.aspectRatioSm, aspectRatioMd = _a.aspectRatioMd, campaign = _a.campaign, categoryTabId = _a.categoryTabId, isPlusEnabled = _a.isPlusEnabled, onCampaignClickCb = _a.onCampaignClick, position = _a.position, _b = _a.trackCreativeName, trackCreativeName = _b === void 0 ? 'open' : _b, _c = _a.trackItemListName, trackItemListName = _c === void 0 ? 'open' : _c, withMobileSpacing = _a.withMobileSpacing, props = __rest(_a, ["aspectRatio", "aspectRatioXs", "aspectRatioSm", "aspectRatioMd", "campaign", "categoryTabId", "isPlusEnabled", "onCampaignClick", "position", "trackCreativeName", "trackItemListName", "withMobileSpacing"]);
    var locale = useLocale();
    var destination = getCampaignUrl(campaign.campaignId, { categoryTabId: categoryTabId });
    var isSmScreen = useMatchMedia("(min-width: ".concat(screenSizes.sm, "px)"));
    var isPlusEarlyAccessCampaign = !!isPlusEnabled && isEarlyAccess(campaign);
    var isPlusExclusiveCampaign = isExclusive(campaign);
    var onCampaignClick = useCallback(function () {
        localStorage.setItem('navigationFrom', 'myLounge');
        var index = position ? position.split('|')[0] : '1';
        trackSelectPromotion({
            creativeName: trackCreativeName,
            itemListName: trackItemListName,
            item: {
                campaignIdentifier: campaign.campaignId,
                categoryId: categoryTabId,
                index: !isSmScreen ? Number(index) : Number(index) - 1,
                earlyAccessItem: isPlusEarlyAccessCampaign ? 1 : 0,
            },
        });
    }, [
        campaign.campaignId,
        categoryTabId,
        isPlusEarlyAccessCampaign,
        isSmScreen,
        position,
        trackCreativeName,
        trackItemListName,
    ]);
    var variant = isPlusEarlyAccessCampaign || isPlusExclusiveCampaign
        ? OpenCampaignCardVariant.Plus
        : OpenCampaignCardVariant.Default;
    var style = {};
    if (withMobileSpacing) {
        style.marginBottom = '3%';
    }
    return (React.createElement(LUXOpenCampaignCard, __assign({ aspectRatio: aspectRatio, aspectRatioXs: aspectRatioXs, aspectRatioSm: aspectRatioSm, aspectRatioMd: aspectRatioMd, "data-position": position, id: "open-".concat(campaign.campaignId), languageCode: locale, name: campaign.name || '', onClick: onCampaignClickCb || onCampaignClick, path: destination, style: style, variant: variant }, props),
        React.createElement(OpenCampaignCardOverlayActive, { cta: React.createElement(Translate, { message: "campaigns.ctaButtonText" }) }),
        React.createElement(CampaignCardContent, { discount: campaign.discount, endsAt: campaign.endsAt, isEarlyAccess: isPlusEarlyAccessCampaign, isExclusive: isPlusExclusiveCampaign }),
        React.createElement(CampaignCardOverlay, { deliveryPromise: campaign.deliveryPromise, hasLogo: campaign.hasLogo, isEarlyAccess: isPlusEarlyAccessCampaign, isExclusive: isPlusExclusiveCampaign }),
        React.createElement(CampaignCardMedia, { aspectRatio: aspectRatio, aspectRatioXs: aspectRatioXs, aspectRatioSm: aspectRatioSm, aspectRatioMd: aspectRatioMd, campaign: campaign })));
});
OpenCampaignCardInternal.displayName = 'OpenCampaignCardInternal';
export var OpenCampaignCard = function (_a) {
    var campaign = _a.campaign, props = __rest(_a, ["campaign"]);
    return (React.createElement(ErrorBoundary, { componentName: "OpenCampaignCard-".concat(campaign.campaignId) },
        React.createElement(OpenCampaignCardInternal, __assign({ campaign: campaign }, props))));
};
OpenCampaignCard.displayName = 'OpenCampaignCard';
